import React,{useEffect,useMemo,useRef,useState} from 'react';
import {AnimatePresence,motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import Modal from './Modal';
import {useTheme} from '../../contexts/ThemeContext';
import {useBookmarks} from '../../contexts/BookmarkContext';
import {getDefaultFaviconUrl,resolveBestFaviconUrl,resizeImage} from '../../utils/favicon';
import pb from '../../pb.js';

const {FiUpload,FiGlobe,FiAlertTriangle,FiEdit2,FiLoader,FiRefreshCw}=FiIcons;
const emptyForm={title:'',url:'',description:'',tags:'',folderId:null,faviconurl:'',faviconfile:null};

const normaliseUrlForComparison=(value)=> {
  const trimmed=String(value || '').trim();
  if (!trimmed) return '';
  try {
    const parsed=new URL(trimmed);
    if (!['http:','https:'].includes(parsed.protocol)) return trimmed.toLowerCase();
    const host=parsed.hostname.toLowerCase().replace(/^www\./,'');
    const port=parsed.port ? `:${parsed.port}` : '';
    const path=parsed.pathname.replace(/\/+$/,'') || '/';
    const sortedParams=Array.from(parsed.searchParams.entries()).sort(([aKey,aValue],[bKey,bValue])=> aKey.localeCompare(bKey) || aValue.localeCompare(bValue));
    const query=new URLSearchParams(sortedParams).toString();
    return `${host}${port}${path}${query ? `?${query}` : ''}`;
  } catch {return trimmed.toLowerCase();}
};

const deriveBasicMetadata=(url)=> {
  try {
    const parsed=new URL(url);
    const domain=parsed.hostname.replace(/^www\./,'');
    return {title:domain.charAt(0).toUpperCase()+domain.slice(1).split('.')[0]};
  } catch {return {title:''};}
};

const isHttpUrl=(value)=> {
  try {return ['http:','https:'].includes(new URL(String(value || '').trim()).protocol);} catch {return false;}
};

const AddBookmarkModal=({isOpen,onClose,editBookmark=null,initialUrl=''})=> {
  const {currentFolder,folders,addBookmark,updateBookmark,bookmarks,setEditingBookmark}=useBookmarks();
  const {theme}=useTheme();
  const [formData,setFormData]=useState(emptyForm);
  const [faviconPreview,setFaviconPreview]=useState(null);
  const [isResolvingFavicon,setIsResolvingFavicon]=useState(false);
  const [faviconLookupFailed,setFaviconLookupFailed]=useState(false);
  const [isSubmitting,setIsSubmitting]=useState(false);
  const [duplicateFound,setDuplicateFound]=useState(null);
  const [tagSuggestions,setTagSuggestions]=useState([]);
  const fileInputRef=useRef(null);
  const urlInputRef=useRef(null);
  const faviconLookupRef=useRef(0);

  const sortedFolders=useMemo(()=> [...folders].sort((a,b)=> (a.name || '').localeCompare(b.name || '')),[folders]);

  const findDuplicate=(url,editingId=editBookmark?.id)=> {
    const key=normaliseUrlForComparison(url);
    if (!key) return null;
    return bookmarks.find(bookmark=> bookmark.id!==editingId && normaliseUrlForComparison(bookmark.url)===key) || null;
  };

  useEffect(()=> {
    if (!isOpen) return;
    faviconLookupRef.current+=1;
    setDuplicateFound(null);
    setTagSuggestions([]);
    setIsSubmitting(false);
    setIsResolvingFavicon(false);
    setFaviconLookupFailed(false);

    if (editBookmark) {
      const next={title:editBookmark.title || '',url:editBookmark.url || '',description:editBookmark.description || '',tags:Array.isArray(editBookmark.tags) ? editBookmark.tags.join(',') : '',folderId:editBookmark.folderId || null,faviconurl:editBookmark.faviconurl || '',faviconfile:undefined};
      setFormData(next);
      if (editBookmark.faviconfile) setFaviconPreview(pb.files.getUrl(editBookmark,editBookmark.faviconfile));
      else setFaviconPreview(editBookmark.faviconurl || null);
      return;
    }

    let capturedUrl='';
    let capturedTitle='';
    try {
      capturedUrl=sessionStorage.getItem('bmp:capture-url') || '';
      capturedTitle=sessionStorage.getItem('bmp:capture-title') || '';
      sessionStorage.removeItem('bmp:capture-url');
      sessionStorage.removeItem('bmp:capture-title');
    } catch {}

    const seedUrl=initialUrl || capturedUrl;
    const metadata=seedUrl ? deriveBasicMetadata(seedUrl) : {title:''};
    const defaultFolder=folders.some(folder=> folder.id===currentFolder) ? currentFolder : null;
    setFormData({...emptyForm,title:capturedTitle || metadata.title,url:seedUrl,folderId:defaultFolder});
    setFaviconPreview(null);
    setDuplicateFound(seedUrl ? findDuplicate(seedUrl,null) : null);
  },[isOpen,editBookmark,currentFolder]);

  useEffect(()=> {
    if (!isOpen || editBookmark || !initialUrl) return;
    setFormData(previous=> {
      if (previous.url.trim()) return previous;
      const metadata=deriveBasicMetadata(initialUrl);
      setDuplicateFound(findDuplicate(initialUrl,null));
      return {...previous,url:initialUrl,title:previous.title || metadata.title,faviconurl:''};
    });
  },[isOpen,editBookmark,initialUrl]);

  useEffect(()=> {
    if (!isOpen || editBookmark) return undefined;
    const finePointer=typeof window!=='undefined' && window.matchMedia?.('(pointer: fine)').matches;
    if (!finePointer || window.innerWidth<1024) return undefined;
    const timer=window.setTimeout(()=> urlInputRef.current?.focus({preventScroll:true}),120);
    return ()=> window.clearTimeout(timer);
  },[isOpen,editBookmark]);

  useEffect(()=> {
    if (!isOpen || formData.faviconfile || editBookmark?.faviconfile) return undefined;
    const url=String(formData.url || '').trim();
    if (!isHttpUrl(url)) {setIsResolvingFavicon(false);return undefined;}

    const lookupId=++faviconLookupRef.current;
    const timer=window.setTimeout(async ()=> {
      setIsResolvingFavicon(true);
      setFaviconLookupFailed(false);
      try {
        const resolved=await resolveBestFaviconUrl(url,{currentUrl:formData.faviconurl || ''});
        if (faviconLookupRef.current!==lookupId) return;
        if (resolved) {
          setFormData(previous=> previous.url.trim()===url ? {...previous,faviconurl:resolved} : previous);
          setFaviconPreview(resolved);
        } else {
          setFaviconLookupFailed(true);
        }
      } catch (error) {
        if (faviconLookupRef.current===lookupId) setFaviconLookupFailed(true);
        console.warn('Automatic favicon lookup failed:',error);
      } finally {
        if (faviconLookupRef.current===lookupId) setIsResolvingFavicon(false);
      }
    },400);

    return ()=> {
      window.clearTimeout(timer);
      if (faviconLookupRef.current===lookupId) faviconLookupRef.current+=1;
    };
  },[isOpen,formData.url,formData.faviconfile,editBookmark?.faviconfile]);

  const handleUrlChange=(event)=> {
    const value=event.target.value;
    const preserveManualIcon=Boolean(formData.faviconfile || editBookmark?.faviconfile);
    setFormData(previous=> ({...previous,url:value,...(!preserveManualIcon ? {faviconurl:''} : {})}));
    if (!preserveManualIcon) setFaviconPreview(null);
    setFaviconLookupFailed(false);
    setDuplicateFound(findDuplicate(value));
  };

  const handleUrlBlur=()=> {
    if (!formData.url || formData.title || editBookmark) return;
    const metadata=deriveBasicMetadata(formData.url);
    setFormData(previous=> ({...previous,title:metadata.title}));
  };

  const handleFindFavicon=async ()=> {
    const url=String(formData.url || '').trim();
    if (!isHttpUrl(url) || formData.faviconfile || editBookmark?.faviconfile || isResolvingFavicon) return;
    const lookupId=++faviconLookupRef.current;
    setIsResolvingFavicon(true);
    setFaviconLookupFailed(false);
    try {
      const resolved=await resolveBestFaviconUrl(url,{currentUrl:''});
      if (faviconLookupRef.current!==lookupId) return;
      if (resolved) {
        setFormData(previous=> ({...previous,faviconurl:resolved}));
        setFaviconPreview(resolved);
      } else {
        setFaviconLookupFailed(true);
      }
    } catch (error) {
      if (faviconLookupRef.current===lookupId) setFaviconLookupFailed(true);
      console.warn('Manual favicon lookup failed:',error);
    } finally {
      if (faviconLookupRef.current===lookupId) setIsResolvingFavicon(false);
    }
  };

  const handleEditExisting=()=> {
    if (!duplicateFound) return;
    const existing=duplicateFound;
    onClose();
    window.setTimeout(()=> setEditingBookmark(existing),100);
  };

  const handleTagChange=(event)=> {
    const value=event.target.value;
    setFormData(previous=> ({...previous,tags:value}));
    if (value.endsWith(',')) {setTagSuggestions([]);return;}
    const currentTags=value.split(',').map(tag=> tag.trim()).filter(Boolean);
    const currentInput=(value.split(',').pop() || '').trim().toLowerCase();
    if (currentInput.length<=1) {setTagSuggestions([]);return;}
    const allTags=new Set();
    bookmarks.forEach(bookmark=> (bookmark.tags || []).forEach(tag=> allTags.add(String(tag))));
    setTagSuggestions([...allTags].filter(tag=> tag.toLowerCase().includes(currentInput) && !currentTags.some(existing=> existing.toLowerCase()===tag.toLowerCase())).slice(0,5));
  };

  const addTag=(tag)=> {
    const parts=formData.tags.split(',');
    parts.pop();
    const next=[...parts.map(item=> item.trim()).filter(Boolean),tag];
    setFormData(previous=> ({...previous,tags:`${next.join(', ')}, `}));
    setTagSuggestions([]);
  };

  const handleSubmit=async (event)=> {
    event.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const trimmedUrl=formData.url.trim();
      const preservesExistingFile=Boolean(editBookmark?.faviconfile && formData.faviconfile===undefined);
      const hasManualFile=Boolean(formData.faviconfile || preservesExistingFile);
      let faviconurl=formData.faviconurl || '';

      if (!hasManualFile && isHttpUrl(trimmedUrl)) {
        setIsResolvingFavicon(true);
        try {
          faviconurl=await resolveBestFaviconUrl(trimmedUrl,{currentUrl:faviconurl}) || faviconurl || getDefaultFaviconUrl(trimmedUrl) || '';
        } finally {
          setIsResolvingFavicon(false);
        }
      }

      const bookmarkData={...formData,url:trimmedUrl,tags:formData.tags.split(',').map(tag=> tag.trim()).filter(Boolean),faviconurl};
      if (editBookmark) await updateBookmark(editBookmark.id,bookmarkData);
      else await addBookmark(bookmarkData);
      onClose();
    } catch (error) {
      console.error('Failed to save bookmark:',error);
    } finally {setIsSubmitting(false);}
  };

  const handleFileUpload=async (event)=> {
    const file=event.target.files?.[0];
    if (!file) return;
    try {
      faviconLookupRef.current+=1;
      const resizedFile=await resizeImage(file);
      setFormData(previous=> ({...previous,faviconfile:resizedFile}));
      setFaviconPreview(URL.createObjectURL(resizedFile));
      setIsResolvingFavicon(false);
      setFaviconLookupFailed(false);
    } catch (error) {console.error('Failed to process image:',error);}
  };

  const fieldClass=`w-full px-4 py-3.5 rounded-2xl border text-base font-bold focus:outline-none focus:ring-4 focus:ring-indigo-500/10 transition-colors ${theme==='dark' ? 'bg-zinc-900 border-zinc-800 text-white placeholder-zinc-700' : 'bg-zinc-50 border-zinc-200 placeholder-zinc-400'}`;
  const hasPreservedCustomIcon=Boolean(formData.faviconfile || editBookmark?.faviconfile);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={editBookmark ? 'Edit Bookmark' : 'Add Bookmark'}>
      <form onSubmit={handleSubmit} className="flex flex-col min-h-full">
        <div className="flex-1 space-y-6 pb-3">
          {duplicateFound && (
            <motion.div initial={{opacity:0,y:-6}} animate={{opacity:1,y:0}} transition={{duration:0.16,ease:'easeOut'}} className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl flex items-start gap-3 transform-gpu">
              <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0"><p className="text-xs font-black uppercase tracking-widest text-amber-900 dark:text-amber-200">Existing Bookmark</p><p className="text-xs text-amber-700 dark:text-amber-400 mt-1 font-medium">This URL is already in {duplicateFound.folderId ? `“${folders.find(folder=> folder.id===duplicateFound.folderId)?.name || 'a folder'}”` : 'Unfiled'}.</p><button type="button" onClick={handleEditExisting} className="mt-3 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-amber-600 dark:text-amber-400"><SafeIcon icon={FiEdit2} className="w-3 h-3" />Edit Existing</button></div>
            </motion.div>
          )}

          <div className="space-y-4">
            <label className="block"><span className="block text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2 ml-1">Bookmark URL</span><input ref={urlInputRef} type="url" required value={formData.url} onChange={handleUrlChange} onBlur={handleUrlBlur} autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="url" className={fieldClass} placeholder="https://example.com" /></label>
            <label className="block"><span className="block text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2 ml-1">Title</span><input type="text" required value={formData.title} onChange={event=> setFormData(previous=> ({...previous,title:event.target.value}))} className={fieldClass} placeholder="Bookmark name" /></label>
            <label className="block"><span className="block text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2 ml-1">Description</span><textarea value={formData.description} onChange={event=> setFormData(previous=> ({...previous,description:event.target.value}))} rows={2} className={`${fieldClass} resize-none`} placeholder="Optional summary..." /></label>

            <div className="relative"><label className="block"><span className="block text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2 ml-1">Tags</span><input type="text" value={formData.tags} onChange={handleTagChange} className={fieldClass} placeholder="work, research, project" /></label>
              <AnimatePresence initial={false}>{tagSuggestions.length>0 && <motion.div initial={{opacity:0,y:4}} animate={{opacity:1,y:0}} exit={{opacity:0,y:4}} transition={{duration:0.14,ease:'easeOut'}} className={`absolute left-0 right-0 mt-2 rounded-2xl border shadow-2xl z-20 overflow-hidden transform-gpu ${theme==='dark' ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-zinc-200'}`}>{tagSuggestions.map(tag=> <button key={tag} type="button" onClick={()=> addTag(tag)} className={`block w-full text-left px-4 py-3 text-xs font-black uppercase tracking-widest ${theme==='dark' ? 'hover:bg-zinc-700 text-zinc-300' : 'hover:bg-indigo-50 text-zinc-600'}`}>{tag}</button>)}</motion.div>}</AnimatePresence>
            </div>

            <label className="block"><span className="block text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2 ml-1">Folder</span><select value={formData.folderId || ''} onChange={event=> setFormData(previous=> ({...previous,folderId:event.target.value || null}))} className={fieldClass}><option value="">Unfiled (Default)</option>{sortedFolders.map(folder=> <option key={folder.id} value={folder.id}>{folder.name}</option>)}</select></label>

            <div className="pt-2"><span className="block text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2 ml-1">Bookmark Icon</span><div className="flex items-center gap-4 p-4 rounded-2xl border border-dashed dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/30"><div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center p-2.5 shadow-xl border dark:border-zinc-800 overflow-hidden flex-shrink-0">{faviconPreview ? <img src={faviconPreview} alt="" className="w-full h-full object-contain" /> : isResolvingFavicon ? <SafeIcon icon={FiRefreshCw} className="w-6 h-6 text-indigo-500 animate-spin" /> : <SafeIcon icon={FiGlobe} className="w-6 h-6 text-zinc-300" />}</div><div className="flex-1 min-w-0"><div className="flex flex-wrap gap-2"><button type="button" onClick={()=> fileInputRef.current?.click()} className={`min-h-11 flex items-center gap-2 px-4 py-2 rounded-xl border text-[10px] font-black uppercase tracking-widest ${theme==='dark' ? 'border-zinc-700 bg-zinc-800' : 'border-zinc-200 bg-white shadow-sm'}`}><SafeIcon icon={FiUpload} className="w-3.5 h-3.5" />Upload Icon</button><button type="button" onClick={handleFindFavicon} disabled={!isHttpUrl(formData.url) || hasPreservedCustomIcon || isResolvingFavicon} className={`min-h-11 flex items-center gap-2 px-4 py-2 rounded-xl border text-[10px] font-black uppercase tracking-widest disabled:opacity-40 ${theme==='dark' ? 'border-zinc-700 bg-zinc-800' : 'border-zinc-200 bg-white shadow-sm'}`}><SafeIcon icon={FiRefreshCw} className={`w-3.5 h-3.5 ${isResolvingFavicon ? 'animate-spin' : ''}`} />Find from Link</button></div><input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" /><p className="text-[9px] text-zinc-500 font-bold mt-2">{hasPreservedCustomIcon ? 'Custom uploaded icon is preserved and takes priority.' : isResolvingFavicon ? 'Checking the website and favicon services…' : faviconLookupFailed ? 'No verified icon found yet. You can retry or upload one.' : faviconPreview ? 'Icon found automatically from the bookmark link.' : 'The app will automatically discover an icon from the link.'}</p></div></div></div>
          </div>
        </div>

        <div className={`sticky bottom-0 z-30 -mx-4 sm:-mx-8 px-4 sm:px-8 pt-4 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] border-t flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 shadow-[0_-18px_28px_-26px_rgba(15,23,42,0.45)] ${theme==='dark' ? 'bg-zinc-950 border-zinc-800' : 'bg-white border-zinc-200'}`}>
          <button type="button" onClick={onClose} className={`order-2 sm:order-1 min-h-12 px-6 rounded-2xl text-xs font-black uppercase tracking-[0.2em] transition-colors ${theme==='dark' ? 'text-zinc-400 hover:bg-zinc-900' : 'text-zinc-500 hover:bg-zinc-100'}`}>Discard</button>
          <button type="submit" disabled={isSubmitting} className="order-1 sm:order-2 min-h-12 px-8 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-[0.2em] text-xs shadow-lg shadow-indigo-500/20 active:bg-indigo-800 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors">{isSubmitting && <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />}<span>{isSubmitting ? 'Saving...' : editBookmark ? 'Update Bookmark' : 'Save Bookmark'}</span></button>
        </div>
      </form>
    </Modal>
  );
};

export default AddBookmarkModal;